import { useSequence } from '../motion/useSequence';
import type { SiteDictionary } from '../content/site-content';

/**
 * ATRIO, shown instead of described.
 *
 * Five beats carry the whole product: a routine question, an answer built from real system data,
 * then a complaint that ATRIO deliberately does not answer. A visitor who reads nothing else on
 * this page still leaves knowing what it does and where it stops.
 *
 * The stage is `aria-hidden`; the same scene reaches assistive technology once, as prose.
 */
export function AtrioDemo({ dictionary, paused }: { dictionary: SiteDictionary; paused: boolean }) {
  const t = (key: string) => dictionary[key] ?? key;
  const step = useSequence(5, 1500, paused);

  return (
    <figure className="demo">
      <p className="visually-hidden">{t('atrio.transcript')}</p>

      <figcaption className="demo__head" aria-hidden="true">
        <span className="demo__source">{t('atrio.channel')}</span>
        <span className="demo__tag">{t('demo.example')}</span>
      </figcaption>

      <div className="chat" aria-hidden="true">
        <p className="chat__line chat__line--in" data-on={step >= 1 || undefined}>
          <span className="chat__who">{t('atrio.who1')}</span>
          <span className="chat__bubble">{t('atrio.msg1')}</span>
        </p>

        <p className="chat__line chat__line--out chat__typing" data-on={step === 2 || undefined}>
          <span className="chat__bubble"><i /><i /><i /></span>
        </p>

        <p className="chat__line chat__line--out" data-on={step >= 3 || undefined}>
          <span className="chat__who">{t('atrio.who2')}</span>
          <span className="chat__bubble">{t('atrio.msg2')}</span>
          <span className="chat__note">{t('atrio.note2')}</span>
        </p>

        <p className="chat__line chat__line--in" data-on={step >= 4 || undefined}>
          <span className="chat__who">{t('atrio.who1')}</span>
          <span className="chat__bubble">{t('atrio.msg3')}</span>
        </p>

        <p className="chat__event" data-on={step >= 5 || undefined}>
          {t('atrio.handoff')}
        </p>
      </div>
    </figure>
  );
}
